import React, { useEffect, useState, useMemo } from 'react';
import AdminLayout from "../admin/AdminDashboardLayout";
import Button from "../../utils/Button";
import Input from "../../utils/Input";
import { useApiQuery } from "../../hooks/useApiQuery";
import { useApiMutation } from "../../hooks/useMutation";
import { ApiRequestOptions } from "../../utils/api";
import toast from 'react-hot-toast';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import AlarmOffIcon from '@mui/icons-material/AlarmOff';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EventIcon from '@mui/icons-material/Event';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import GroupIcon from '@mui/icons-material/Group';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LoginIcon from '@mui/icons-material/Login';
import AlarmOnIcon from '@mui/icons-material/AlarmOn';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import TimerOutlinedIcon from '@mui/icons-material/TimerOutlined';
import { MenuItem, Select, FormControl, InputLabel, Chip } from '@mui/material';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import "react-datepicker/dist/react-datepicker.css";
import { PartnerResponse } from '../../types/Settings';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import PersonPinIcon from '@mui/icons-material/PersonPin';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import SpeedIcon from '@mui/icons-material/Speed';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SecurityIcon from '@mui/icons-material/Security';
import SettingsIcon from '@mui/icons-material/Settings';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import jsPDF from 'jspdf';
import { useDashboardRole } from '../../hooks/useDashboardRole';
import voteLogo from '../../assets/images/123votelogo.png';

type MemberRole = "CANVASSER" | "SUPERVISOR" | "PARTNER_ADMIN" | "PARTNER_MEMBER" | string;

type PartnerMember = {
  id: number;
  fullName: string;
  email: string;
  phone: string | null;
  createdAt: string;
  lastLoginAt?: string;
  lastLoginLocation?: string;
  enabled: boolean;
  suspended?: boolean;
  onShift: boolean;
  totalRegistrations: number;
  role: MemberRole;
};

const isCanvasserMember = (member: Pick<PartnerMember, 'role'>) => {
  return member.role?.toUpperCase() === 'CANVASSER';
};

const isSupervisorMember = (member: Pick<PartnerMember, 'role'>) => {
  return member.role?.toUpperCase() === 'SUPERVISOR';
};

const isCanvasserSuspended = (member: Pick<PartnerMember, 'suspended'>) => !!member.suspended;

const isCanvasserPermanentlyDeactivated = (member: Pick<PartnerMember, 'enabled'>) => !member.enabled;

const isCanvasserInactive = (member: Pick<PartnerMember, 'enabled' | 'suspended'>) => {
  return isCanvasserPermanentlyDeactivated(member) || isCanvasserSuspended(member);
};

const isSupervisorActive = (member: Pick<PartnerMember, 'enabled' | 'suspended' | 'role'>) => {
  return member.enabled && member.role?.toUpperCase() === 'SUPERVISOR' && !member.suspended;
};

const isCanvasserActive = (member: Pick<PartnerMember, 'enabled' | 'suspended' | 'role'>) => {
  return member.enabled && member.role?.toUpperCase() === 'CANVASSER' && !member.suspended;
};

type MembersResponse = {
  success: boolean;
  message: string;
  data: {
    content: PartnerMember[];
    pageNumber: number;
    pageSize: number;
    totalElements: number;
    totalPages: number;
    first: boolean;
    last: boolean;
  };
};

type CanvasserTrackingRecord = {
  userId: number;
  fullName: string;
  email: string;
  phone: string | null;
  periodStart?: string | null;
  periodEndExclusive?: string | null;
  totalShifts?: number | null;
  totalLocations?: number | null;
  totalEngagements?: number | null;
  totalShiftDurationSeconds?: number | null;
  activeEngagementDurationSeconds?: number | null;
  distanceTravelledInKm: number | null
};

type CanvasserTrackingResponse = {
  success?: boolean;
  message?: string;
  data?: CanvasserTrackingRecord;
};

type TrackingMetricCard = {
  label: string;
  value: string;
  icon: typeof AccessTimeIcon;
  accent: string;
  tone: string;
};

type CanvasserPerformanceData = {
  id: number;
  partnerId: number;
  canvasserId: number;
  totalFormsClaimed: number;
  totalVerifiedForms: number;
  totalRejectedForms: number;
  averageAccuracyScore: number;
  averageTimelinessScore: number;
  averageRejectionRate: number;
  finalPerformanceScore: number;
  riskTier: string;
};

type CanvasserPerformanceResponse = {
  success: boolean;
  message: string;
  data: CanvasserPerformanceData[];
};

type SupervisorPerformanceData = {
  supervisorId: number;
  assignedBatches: number;
  verifiedBatches: number;
  submittedBatches: number;
  deliveredBatches: number;
  formsVerified: number;
  formsDeliveredToElectionBody: number;
  avgHoursToVerify: number | null;
  overdueBatches: number;
  pendingBatches: number;
};

type SupervisorPerformanceResponse = {
  success: boolean;
  message: string;
  data: SupervisorPerformanceData;
};

type PerformanceMetricCard = {
  label: string;
  value: string;
  icon: typeof AccessTimeIcon;
  accent: string;
  tone: string;
};

export default function Canvassers() {
  const { dashboardRole, isSupervisor } = useDashboardRole();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedCanvasser, setSelectedCanvasser] = useState<PartnerMember | null>(null);
  const [trackingStartDate, setTrackingStartDate] = useState<Date | null>(null);
  const [trackingEndDate, setTrackingEndDate] = useState<Date | null>(null);
  const [appliedTrackingRange, setAppliedTrackingRange] = useState<{ startDate: string; endDate: string } | null>(null);
  const [showSeatModal, setShowSeatModal] = useState(false);
  const [showManageModal, setShowManageModal] = useState(false);
  const [showSuspendConfirmModal, setShowSuspendConfirmModal] = useState(false);
  const [showDeactivateConfirmModal, setShowDeactivateConfirmModal] = useState(false);
  const [showUpgradeConfirmModal, setShowUpgradeConfirmModal] = useState(false);
  const [manageTargetMember, setManageTargetMember] = useState<PartnerMember | null>(null);
  const [pendingSuspendAction, setPendingSuspendAction] = useState<'suspend' | 'unsuspend' | null>(null);
  const [seatQuantity, setSeatQuantity] = useState(1);
  const [renewalMessage, setRenewalMessage] = useState('');
  const [isExportingPdf, setIsExportingPdf] = useState(false);
  const [editingField, setEditingField] = useState<'email' | 'phone' | null>(null);
  const [editableEmail, setEditableEmail] = useState('');
  const [editablePhone, setEditablePhone] = useState('');
  const todayDate = format(new Date(), 'yyyy-MM-dd');
  const effectiveTrackingStartDate = appliedTrackingRange?.startDate ?? todayDate;
  const effectiveTrackingEndDate = appliedTrackingRange?.endDate ?? todayDate;
  const selectedMemberRole = selectedCanvasser?.role?.toUpperCase();
  const isSelectedMemberCanvasser = selectedMemberRole === 'CANVASSER';
  const isSelectedMemberSupervisor = selectedMemberRole === 'SUPERVISOR';

  // Fetch partner members from API
  const membersQuery = useApiQuery<MembersResponse>({
    queryKey: ["partner-members"],
    request: {
      method: "GET",
      url: "/partner/members",
    } as ApiRequestOptions,
  });

  const partnerQuery = useApiQuery<PartnerResponse>({
    queryKey: ["partner", "status"],
    request: {
      method: "GET",
      url: "/partner"
    } as ApiRequestOptions,
  });

  const trackingQuery = useApiQuery<CanvasserTrackingResponse>({
    queryKey: ["canvasser-tracking", selectedCanvasser?.id, effectiveTrackingStartDate, effectiveTrackingEndDate],
    request: {
      method: "GET",
      url: selectedCanvasser && `/partner/admin/field-activity/users/${selectedCanvasser.id}`,
      params: {
        startDate: effectiveTrackingStartDate,
        endDate: effectiveTrackingEndDate,
      },
    } as ApiRequestOptions,
    enabled: showDetailsModal && !!selectedCanvasser?.id,
    retry: false,
  });

  const performanceQuery = useApiQuery<CanvasserPerformanceResponse>({
    queryKey: ["canvasser-performance", selectedCanvasser?.id],
    request: {
      method: "GET",
      url: selectedCanvasser && `/canvasser-performance`,
      params: {
        canvasserId: selectedCanvasser?.id,
      },
    } as ApiRequestOptions,
    enabled: showDetailsModal && !!selectedCanvasser?.id && isSelectedMemberCanvasser,
    retry: false,
  });

  const supervisorPerformanceQuery = useApiQuery<SupervisorPerformanceResponse>({
    queryKey: ["supervisor-performance", selectedCanvasser?.id],
    request: {
      method: "GET",
      url: selectedCanvasser && `/supervisor-performance`,
      params: {
        supervisorId: selectedCanvasser?.id,
      },
    } as ApiRequestOptions,
    enabled: showDetailsModal && !!selectedCanvasser?.id && isSelectedMemberSupervisor,
    retry: false,
  });

  const suspendUserMutation = useApiMutation<{
    success: boolean;
    message: string;
  }>({});

  const unsuspendUserMutation = useApiMutation<{
    success: boolean;
    message: string;
  }>({});

  const deactivateMemberMutation = useApiMutation<{
    success: boolean;
    message: string;
  }>({});

  const upgradeMemberRoleMutation = useApiMutation<{
    success: boolean;
    message: string;
    data?: PartnerMember;
  }>({});

  const addSeatsMutation = useApiMutation<{
    success: boolean;
    message: string;
    data: {
      seatsAdded: number;
      totalExtraSeats: number;
      proratedChargeDescription: string;
      renewalDescription: string;
      message: string;
    };
  }>({});

  const updateCanvasserMutation = useApiMutation<{
    success: boolean;
    message: string;
    data?: PartnerMember;
  }>({});

  const membersData = useMemo(
    () => membersQuery.data?.data.content || [],
    [membersQuery.data?.data.content]
  );
  const isLoading = membersQuery.isLoading;
  const isError = membersQuery.isError;

  const canvassers = useMemo(
    () => (isSupervisor ? membersData.filter(isCanvasserMember) : membersData),
    [isSupervisor, membersData]
  );

  const activeSupervisors = useMemo(
    () => membersData.filter(member => isSupervisorMember(member) && isSupervisorActive(member)),
    [membersData]
  );

  const filteredCanvassers = useMemo(() => {
    return canvassers.filter(canvasser => {
      const isInactive = isCanvasserInactive(canvasser);
      const normalizedSearch = searchTerm.toLowerCase();
      const matchesSearch = canvasser.fullName.toLowerCase().includes(normalizedSearch) ||
        canvasser.email.toLowerCase().includes(normalizedSearch) ||
        canvasser.role?.toLowerCase().includes(normalizedSearch) ||
        (canvasser.lastLoginLocation?.toLowerCase().includes(normalizedSearch) || false);

      const matchesStatus = statusFilter === 'all' ||
        (statusFilter === 'active' && isCanvasserActive(canvasser)) ||
        (statusFilter === 'inactive' && isInactive);

      return matchesSearch && matchesStatus;
    });
  }, [searchTerm, statusFilter, canvassers]);

  const getStatusColor = (member: Pick<PartnerMember, 'enabled' | 'suspended'>) => {
    if (isCanvasserPermanentlyDeactivated(member)) return 'bg-red-100 text-red-800';
    if (isCanvasserSuspended(member)) return 'bg-amber-100 text-amber-800';
    return 'bg-green-100 text-green-800';
  };

  const getStatusLabel = (member: Pick<PartnerMember, 'enabled' | 'suspended'>) => {
    if (isCanvasserPermanentlyDeactivated(member)) return 'Deactivated';
    if (isCanvasserSuspended(member)) return 'Suspended';
    return 'Active';
  };

  const formatRoleLabel = (role?: string) => {
    if (!role) return 'Member';

    return role
      .toLowerCase()
      .split('_')
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ');
  };

  const getRoleColor = (role?: string) => {
    switch (role?.toUpperCase()) {
      case 'CANVASSER':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'SUPERVISOR':
        return 'bg-violet-100 text-violet-800 border-violet-200';
      case 'PARTNER_ADMIN':
        return 'bg-slate-100 text-slate-800 border-slate-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'MMM dd, yyyy');
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString?: string | null) => {
    if (!dateString) {
      return 'Unavailable';
    }

    try {
      return format(new Date(dateString), 'MMM dd, yyyy • h:mm a');
    } catch {
      return String(dateString);
    }
  };

  const formatDuration = (value?: string | number | null) => {
    if (value === null || value === undefined || value === '') {
      return '0';
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      const totalSeconds = Math.max(0, Math.floor(value));
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      const parts: string[] = [];

      if (hours > 0) {
        parts.push(`${hours} hr${hours === 1 ? '' : 's'}`);
      }

      if (minutes > 0 || hours > 0) {
        parts.push(`${minutes} min${minutes === 1 ? '' : 's'}`);
      }

      parts.push(`${seconds} sec${seconds === 1 ? '' : 's'}`);

      return parts.join(', ');
    }

    return String(value);
  };

  const trackingData = trackingQuery.data?.data;
  const hasTrackingError = trackingQuery.isError;
  const performanceData = performanceQuery.data?.data?.[0];
  const hasPerformanceError = performanceQuery.isError;
  const supervisorPerformanceData = supervisorPerformanceQuery.data?.data;
  const hasSupervisorPerformanceError = supervisorPerformanceQuery.isError;

  const trackingCards: TrackingMetricCard[] = useMemo(() => {
    const cards: TrackingMetricCard[] = [
      {
        label: 'Period Start',
        value: hasTrackingError ? 'Unavailable' : formatDateTime(trackingData?.periodStart),
        icon: AlarmOnIcon,
        accent: 'from-sky-500 to-blue-600',
        tone: 'bg-sky-50 text-sky-700 border-sky-100',
      },
      {
        label: 'Period End',
        value: hasTrackingError ? 'Unavailable' : formatDateTime(trackingData?.periodEndExclusive),
        icon: AlarmOffIcon,
        accent: 'from-emerald-500 to-teal-600',
        tone: 'bg-emerald-50 text-emerald-700 border-emerald-100',
      },
      {
        label: 'Active Engagement Time',
        value: hasTrackingError ? '0' : formatDuration(trackingData?.activeEngagementDurationSeconds),
        icon: AccessTimeIcon,
        accent: 'from-fuchsia-500 to-violet-600',
        tone: 'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-100',
      },
      {
        label: 'Total Shifts',
        value: hasTrackingError ? '0' : String(trackingData?.totalShifts ?? 0),
        icon: PersonPinIcon,
        accent: 'from-amber-500 to-orange-600',
        tone: 'bg-amber-50 text-amber-700 border-amber-100',
      },
      {
        label: 'Total Radius of Engagement',
        value: hasTrackingError ? '0' : String(trackingData?.distanceTravelledInKm ?? 0),
        icon: MyLocationIcon,
        accent: 'from-rose-500 to-pink-600',
        tone: 'bg-rose-50 text-rose-700 border-rose-100',
      },
      {
        label: 'Total Shift Duration',
        value: hasTrackingError ? '0' : formatDuration(trackingData?.totalShiftDurationSeconds),
        icon: TimerOutlinedIcon,
        accent: 'from-slate-700 to-slate-900',
        tone: 'bg-slate-100 text-slate-700 border-slate-200',
      },
    ];

    return cards;
  }, [hasTrackingError, trackingData]);

  const performanceCards: PerformanceMetricCard[] = useMemo(() => {
    if (!performanceData || hasPerformanceError) {
      return [
        {
          label: 'Total Forms Claimed',
          value: '0',
          icon: AssessmentIcon,
          accent: 'from-blue-500 to-indigo-600',
          tone: 'bg-blue-50 text-blue-700 border-blue-100',
        },
        {
          label: 'Verified Forms',
          value: '0',
          icon: VerifiedUserIcon,
          accent: 'from-green-500 to-emerald-600',
          tone: 'bg-green-50 text-green-700 border-green-100',
        },
        {
          label: 'Rejected Forms',
          value: '0',
          icon: ThumbDownIcon,
          accent: 'from-red-500 to-rose-600',
          tone: 'bg-red-50 text-red-700 border-red-100',
        },
        {
          label: 'Accuracy Score',
          value: '0%',
          icon: SpeedIcon,
          accent: 'from-purple-500 to-violet-600',
          tone: 'bg-purple-50 text-purple-700 border-purple-100',
        },
        {
          label: 'Timeliness Score',
          value: '0%',
          icon: AccessTimeIcon,
          accent: 'from-amber-500 to-orange-600',
          tone: 'bg-amber-50 text-amber-700 border-amber-100',
        },
        {
          label: 'Performance Score',
          value: '0',
          icon: TrendingUpIcon,
          accent: 'from-cyan-500 to-teal-600',
          tone: 'bg-cyan-50 text-cyan-700 border-cyan-100',
        },
      ];
    }

    const cards: PerformanceMetricCard[] = [
      {
        label: 'Total Forms Claimed',
        value: String(performanceData.totalFormsClaimed || 0),
        icon: AssessmentIcon,
        accent: 'from-blue-500 to-indigo-600',
        tone: 'bg-blue-50 text-blue-700 border-blue-100',
      },
      {
        label: 'Verified Forms',
        value: String(performanceData.totalVerifiedForms || 0),
        icon: VerifiedUserIcon,
        accent: 'from-green-500 to-emerald-600',
        tone: 'bg-green-50 text-green-700 border-green-100',
      },
      {
        label: 'Rejected Forms',
        value: String(performanceData.totalRejectedForms || 0),
        icon: ThumbDownIcon,
        accent: 'from-red-500 to-rose-600',
        tone: 'bg-red-50 text-red-700 border-red-100',
      },
      {
        label: 'Accuracy Score',
        value: `${performanceData.averageAccuracyScore?.toFixed(2) || '0.00'}%`,
        icon: SpeedIcon,
        accent: 'from-purple-500 to-violet-600',
        tone: 'bg-purple-50 text-purple-700 border-purple-100',
      },
      {
        label: 'Timeliness Score',
        value: `${performanceData.averageTimelinessScore?.toFixed(2) || '0.00'}%`,
        icon: AccessTimeIcon,
        accent: 'from-amber-500 to-orange-600',
        tone: 'bg-amber-50 text-amber-700 border-amber-100',
      },
      {
        label: 'Performance Score',
        value: String(performanceData.finalPerformanceScore?.toFixed(2) || '0.00'),
        icon: TrendingUpIcon,
        accent: 'from-cyan-500 to-teal-600',
        tone: 'bg-cyan-50 text-cyan-700 border-cyan-100',
      },
    ];

    return cards;
  }, [performanceData, hasPerformanceError]);

  const supervisorPerformanceCards: PerformanceMetricCard[] = useMemo(() => {
    const metrics = supervisorPerformanceData;

    return [
      {
        label: 'Assigned Batches',
        value: String(metrics?.assignedBatches ?? 0),
        icon: AssessmentIcon,
        accent: 'from-blue-500 to-indigo-600',
        tone: 'bg-blue-50 text-blue-700 border-blue-100',
      },
      {
        label: 'Total Pending Batches',
        value: String(metrics?.pendingBatches ?? 0),
        icon: FlagRoundedIcon,
        accent: 'from-rose-500 to-pink-600',
        tone: 'bg-rose-50 text-rose-700 border-rose-100',
      },
      {
        label: 'Total Verified Batches',
        value: String(metrics?.verifiedBatches ?? 0),
        icon: VerifiedUserIcon,
        accent: 'from-green-500 to-emerald-600',
        tone: 'bg-green-50 text-green-700 border-green-100',
      },
      {
        label: 'Total Batch Forms Verified',
        value: String(metrics?.formsVerified ?? 0),
        icon: SecurityIcon,
        accent: 'from-amber-500 to-orange-600',
        tone: 'bg-amber-50 text-amber-700 border-amber-100',
      },
      {
        label: 'Delivered Batches to SOE',
        value: String(metrics?.deliveredBatches ?? 0),
        icon: FlagRoundedIcon,
        accent: 'from-purple-500 to-violet-600',
        tone: 'bg-purple-50 text-purple-700 border-purple-100',
      },
      {
        label: 'Total Forms Delivered To SOE',
        value: String(metrics?.formsDeliveredToElectionBody ?? 0),
        icon: FlagRoundedIcon,
        accent: 'from-green-500 to-emerald-600',
        tone: 'bg-lime-50 text-lime-700 border-lime-100',
      },

      {
        label: 'Total Overdue Batches',
        value: String(metrics?.overdueBatches ?? 0),
        icon: FlagRoundedIcon,
        accent: 'from-rose-500 to-pink-600',
        tone: 'bg-rose-50 text-rose-700 border-rose-100',
      },
      {
        label: 'Avg Hours To Verify',
        value: metrics?.avgHoursToVerify === null || metrics?.avgHoursToVerify === undefined
          ? '--'
          : String(Number(metrics.avgHoursToVerify).toFixed(2)),
        icon: AccessTimeIcon,
        accent: 'from-slate-700 to-slate-900',
        tone: 'bg-slate-100 text-slate-700 border-slate-200',
      },
    ];
  }, [supervisorPerformanceData]);

  const handleInviteCanvasser = () => {
    setShowInviteModal(true);
  };

  // const handleDeleteCanvasser = (canvasserId: number) => {
  //   toast.success('Canvasser deleted successfully');
  // };

  useEffect(() => {
    if (!selectedCanvasser) {
      return;
    }

    const updatedCanvasser = canvassers.find((member) => member.id === selectedCanvasser.id);
    if (updatedCanvasser) {
      setSelectedCanvasser(updatedCanvasser);
    } else if (isSupervisor) {
      setShowDetailsModal(false);
      setSelectedCanvasser(null);
    }
  }, [canvassers, isSupervisor, selectedCanvasser]);

  const closeManageFlow = () => {
    setShowManageModal(false);
    setShowSuspendConfirmModal(false);
    setShowDeactivateConfirmModal(false);
    setShowUpgradeConfirmModal(false);
    setManageTargetMember(null);
    setPendingSuspendAction(null);
  };

  const openManageModal = (member: PartnerMember) => {
    setManageTargetMember(member);
    setShowManageModal(true);
  };

  const handleSuspendActionRequest = (action: 'suspend' | 'unsuspend') => {
    setPendingSuspendAction(action);
    setShowManageModal(false);
    setShowSuspendConfirmModal(true);
  };

  const handleDeactivateActionRequest = () => {
    setShowManageModal(false);
    setShowDeactivateConfirmModal(true);
  };

  const handleUpgradeActionRequest = () => {
    if (!manageTargetMember || !isCanvasserMember(manageTargetMember) || isSupervisor) {
      return;
    }

    setShowManageModal(false);
    setShowUpgradeConfirmModal(true);
  };

  const handleConfirmSuspendAction = () => {
    if (!manageTargetMember || !pendingSuspendAction) {
      return;
    }

    const isUnsuspend = pendingSuspendAction === 'unsuspend';
    const endpoint = isUnsuspend
      ? `/admin/users/${manageTargetMember.id}/unsuspend`
      : `/admin/users/${manageTargetMember.id}/suspend`;
    const mutation = isUnsuspend ? unsuspendUserMutation : suspendUserMutation;

    mutation.mutate(
      {
        method: "PATCH",
        url: endpoint,
      } as ApiRequestOptions,
      {
        onSuccess: async () => {
          await membersQuery.refetch();
          await partnerQuery.refetch();
          toast.success(`Member ${isUnsuspend ? 'unsuspended' : 'suspended'} successfully`);
          closeManageFlow();
        },
        onError: (error: any) => {
          toast.error(error?.response?.data?.message || error?.message || `Failed to ${isUnsuspend ? 'unsuspend' : 'suspend'} member`);
        },
      }
    );
  };

  const handleConfirmDeactivateAction = () => {
    if (!manageTargetMember) {
      return;
    }

    deactivateMemberMutation.mutate(
      {
        method: "PATCH",
        url: `/partner/members/${manageTargetMember.id}/deactivate`,
      } as ApiRequestOptions,
      {
        onSuccess: async () => {
          await membersQuery.refetch();
          await partnerQuery.refetch();
          toast.success('Member deactivated and seat released successfully');
          if (selectedCanvasser?.id === manageTargetMember.id) {
            setShowDetailsModal(false);
            setSelectedCanvasser(null);
          }
          closeManageFlow();
        },
        onError: (error: any) => {
          toast.error(error?.response?.data?.message || error?.message || 'Failed to deactivate member');
        },
      }
    );
  };

  const handleConfirmUpgradeAction = () => {
    if (!manageTargetMember || !isCanvasserMember(manageTargetMember) || isSupervisor) {
      return;
    }

    upgradeMemberRoleMutation.mutate(
      {
        method: "PUT",
        url: `/partner/members/${manageTargetMember.id}`,
        data: {
          role: "SUPERVISOR",
        },
      } as ApiRequestOptions,
      {
        onSuccess: async () => {
          await membersQuery.refetch();
          await partnerQuery.refetch();
          toast.success("Canvasser upgraded to supervisor successfully");
          if (selectedCanvasser?.id === manageTargetMember.id) {
            setSelectedCanvasser((prev) => prev ? { ...prev, role: "SUPERVISOR" } : prev);
          }
          closeManageFlow();
        },
        onError: (error: any) => {
          toast.error(error?.response?.data?.message || error?.message || "Failed to upgrade canvasser to supervisor");
        },
      }
    );
  };

  const handleShowDetails = (canvasser: PartnerMember) => {
    setSelectedCanvasser(canvasser);
    setEditableEmail(canvasser.email);
    setEditablePhone(canvasser.phone || '');
    setEditingField(null);
    setTrackingStartDate(null);
    setTrackingEndDate(null);
    setAppliedTrackingRange(null);
    setShowDetailsModal(true);
  };

  const handleCloseDetails = () => {
    setShowDetailsModal(false);
    setSelectedCanvasser(null);
    setEditableEmail('');
    setEditablePhone('');
    setEditingField(null);
    setTrackingStartDate(null);
    setTrackingEndDate(null);
    setAppliedTrackingRange(null);
  };

  const handleTrackingFilter = () => {
    if (!trackingStartDate || !trackingEndDate) {
      toast.error('Select both start and end dates to filter field activity.');
      return;
    }

    setAppliedTrackingRange({
      startDate: format(trackingStartDate, 'yyyy-MM-dd'),
      endDate: format(trackingEndDate, 'yyyy-MM-dd'),
    });
  };

  const handleResetTrackingFilter = () => {
    setTrackingStartDate(null);
    setTrackingEndDate(null);
    setAppliedTrackingRange(null);
  };

  const handleStartEditing = (field: 'email' | 'phone') => {
    if (!selectedCanvasser) {
      return;
    }

    if (field === 'email') {
      setEditableEmail(selectedCanvasser.email);
    } else {
      setEditablePhone(selectedCanvasser.phone || '');
    }

    setEditingField(field);
  };

  const handleCancelEditing = () => {
    if (selectedCanvasser) {
      setEditableEmail(selectedCanvasser.email);
      setEditablePhone(selectedCanvasser.phone || '');
    }

    setEditingField(null);
  };

  const handleSaveContactField = (field: 'email' | 'phone') => {
    if (!selectedCanvasser) {
      return;
    }

    const trimmedEmail = editableEmail.trim();
    const trimmedPhone = editablePhone.trim();

    if (field === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!trimmedEmail || !emailRegex.test(trimmedEmail)) {
        toast.error('Enter a valid email address.');
        return;
      }
    }

    if (field === 'phone' && trimmedPhone) {
      const phoneRegex = /^[\d\s()+-]{7,20}$/;
      if (!phoneRegex.test(trimmedPhone)) {
        toast.error('Enter a valid phone number.');
        return;
      }
    }

    const payload = field === 'email'
      ? { email: trimmedEmail }
      : { phone: trimmedPhone };

    updateCanvasserMutation.mutate(
      {
        method: 'PUT',
        url: `/partner/members/${selectedCanvasser.id}`,
        data: payload,
      } as ApiRequestOptions,
      {
        onSuccess: () => {
          setSelectedCanvasser((prev) => (
            prev
              ? {
                ...prev,
                email: field === 'email' ? trimmedEmail : prev.email,
                phone: field === 'phone' ? (trimmedPhone || null) : prev.phone,
              }
              : prev
          ));
          setEditingField(null);
          membersQuery.refetch();
          toast.success(`${field === 'email' ? 'Email' : 'Phone number'} updated successfully`);
        },
        onError: () => {
          toast.error(`Failed to update ${field === 'email' ? 'email' : 'phone number'}`);
        },
      }
    );
  };

  const handleExportCanvasserPdf = async () => {
    if (!selectedCanvasser) {
      return;
    }

    setIsExportingPdf(true);

    try {
      const watermarkImage = await new Promise<HTMLImageElement>((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Unable to load 123 logo watermark'));
        image.src = voteLogo;
      });

      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'a4',
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 40;
      const contentWidth = pageWidth - (margin * 2);
      const leftColumnWidth = (contentWidth - 24) / 2;
      const rightColumnX = margin + leftColumnWidth + 24;
      let y = margin;

      const setPdfOpacity = (opacity: number) => {
        const pdfWithGraphicsState = pdf as jsPDF & {
          GState?: new (options: { opacity: number }) => unknown;
          setGState?: (gState: unknown) => void;
        };

        if (pdfWithGraphicsState.GState && pdfWithGraphicsState.setGState) {
          pdfWithGraphicsState.setGState(new pdfWithGraphicsState.GState({ opacity }));
        }
      };

      const drawWatermark = () => {
        const logoWidth = Math.min(240, pageWidth * 0.42);
        const logoHeight = logoWidth * (watermarkImage.naturalHeight / watermarkImage.naturalWidth);
        const logoX = (pageWidth - logoWidth) / 2;
        const logoY = (pageHeight - logoHeight) / 2;

        setPdfOpacity(0.15);
        pdf.addImage(watermarkImage, 'PNG', logoX, logoY, logoWidth, logoHeight, undefined, 'FAST');
        setPdfOpacity(1);
      };

      const ensureSpace = (spaceNeeded: number) => {
        if (y + spaceNeeded <= pageHeight - margin) {
          return;
        }

        pdf.addPage();
        drawWatermark();
        y = margin;
      };

      const drawDivider = () => {
        ensureSpace(20);
        pdf.setDrawColor(226, 232, 240);
        pdf.line(margin, y, pageWidth - margin, y);
        y += 18;
      };

      const drawSectionTitle = (title: string, subtitle?: string) => {
        ensureSpace(subtitle ? 50 : 28);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(14);
        pdf.setTextColor(15, 23, 42);
        pdf.text(title, margin, y);
        y += 18;

        if (subtitle) {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(10);
          pdf.setTextColor(100, 116, 139);
          const lines = pdf.splitTextToSize(subtitle, contentWidth);
          pdf.text(lines, margin, y);
          y += lines.length * 12 + 6;
        }
      };

      const drawField = (label: string, value: string, x: number, width: number) => {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.setTextColor(100, 116, 139);
        pdf.text(label.toUpperCase(), x, y);

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(11);
        pdf.setTextColor(15, 23, 42);
        const valueLines = pdf.splitTextToSize(value, width);
        pdf.text(valueLines, x, y + 16);

        return 16 + (valueLines.length * 13);
      };

      const drawMetricGrid = (metrics: Array<{ label: string; value: string }>) => {
        for (let index = 0; index < metrics.length; index += 2) {
          ensureSpace(52);
          const leftMetric = metrics[index];
          const rightMetric = metrics[index + 1];

          const rowHeight = Math.max(
            drawField(leftMetric.label, leftMetric.value, margin, leftColumnWidth),
            rightMetric ? drawField(rightMetric.label, rightMetric.value, rightColumnX, leftColumnWidth) : 0
          );

          y += rowHeight + 8;
        }
      };

      const drawNotice = (message: string) => {
        ensureSpace(42);
        pdf.setFont('helvetica', 'italic');
        pdf.setFontSize(10);
        pdf.setTextColor(180, 83, 9);
        const noticeLines = pdf.splitTextToSize(message, contentWidth);
        pdf.text(noticeLines, margin, y);
        y += noticeLines.length * 12 + 8;
      };

      const trackingStatus = trackingQuery.isLoading
        ? 'Loading'
        : trackingQuery.isError
          ? 'Fallback data'
          : 'Synced';

      const appliedRangeLabel = appliedTrackingRange
        ? `${format(trackingStartDate || new Date(appliedTrackingRange.startDate), 'MMM dd, yyyy')} to ${format(trackingEndDate || new Date(appliedTrackingRange.endDate), 'MMM dd, yyyy')}`
        : `Today (${format(new Date(), 'MMM dd, yyyy')})`;

      drawWatermark();

      pdf.setFillColor(15, 23, 42);
      pdf.rect(0, 0, pageWidth, 100, 'F');

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(22);
      pdf.setTextColor(255, 255, 255);
      pdf.text(`${formatRoleLabel(selectedCanvasser.role)} Details Report`, margin, 50);

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(11);
      pdf.setTextColor(203, 213, 225);
      pdf.text(`Generated ${format(new Date(), 'MMM dd, yyyy h:mm a')}`, margin, 72);

      y = 128;

      drawSectionTitle(
        selectedCanvasser.fullName,
        'Profile information, field activity, and role-specific performance exported from the member details modal.'
      );

      const profileRowHeight = Math.max(
        drawField('Role', formatRoleLabel(selectedCanvasser.role), margin, leftColumnWidth),
        drawField('Status', getStatusLabel(selectedCanvasser), rightColumnX, leftColumnWidth)
      );
      y += profileRowHeight + 8;

      const workRowHeight = Math.max(
        drawField('Shift Status', selectedCanvasser.onShift ? 'On Shift' : 'Off Shift', margin, leftColumnWidth),
        drawField('Total Registrations', selectedCanvasser.totalRegistrations.toLocaleString(), rightColumnX, leftColumnWidth)
      );
      y += workRowHeight + 8;

      const identityRowHeight = Math.max(
        drawField('Email Address', selectedCanvasser.email, margin, leftColumnWidth),
        drawField('Phone Number', selectedCanvasser.phone || 'No phone', rightColumnX, leftColumnWidth)
      );
      y += identityRowHeight + 8;

      const locationRowHeight = Math.max(
        drawField('Last Known Location', selectedCanvasser.lastLoginLocation || 'Unknown', margin, leftColumnWidth),
        drawField('Join Date', formatDate(selectedCanvasser.createdAt), rightColumnX, leftColumnWidth)
      );
      y += locationRowHeight + 8;

      drawDivider();

      drawSectionTitle(
        'Tracking & Session Summary',
        `Applied tracking range: ${appliedRangeLabel}`
      );

      const trackingStatusRowHeight = drawField('Tracking Status', trackingStatus, margin, leftColumnWidth);
      y += trackingStatusRowHeight + 8;

      drawMetricGrid(trackingCards.map((card) => ({
        label: card.label,
        value: card.value,
      })));

      if (trackingQuery.isError) {
        drawNotice('Live tracking data was unavailable at export time. The report includes fallback values where necessary.');
      }

      if (trackingQuery.isLoading) {
        drawNotice('Live tracking data was still loading at export time. The report includes the values available in the modal at the time of export.');
      }

      if (isSelectedMemberCanvasser) {
        drawDivider();
        drawSectionTitle(
          'Canvasser Performance',
          'Key performance indicators and quality metrics for this canvasser.'
        );

        const riskTierRowHeight = drawField(
          'Risk Tier',
          performanceQuery.isLoading ? 'Loading' : (performanceData?.riskTier || 'UNKNOWN'),
          margin,
          leftColumnWidth
        );
        y += riskTierRowHeight + 8;

        drawMetricGrid(performanceCards.map((card) => ({
          label: card.label,
          value: card.value,
        })));

        if (performanceQuery.isError) {
          drawNotice('Performance data was unavailable at export time. The report includes fallback values where necessary.');
        }

        if (performanceQuery.isLoading) {
          drawNotice('Performance data was still loading at export time. The report includes the values available in the modal at the time of export.');
        }
      }

      if (isSelectedMemberSupervisor) {
        drawDivider();
        drawSectionTitle(
          'Supervisor Performance',
          'Batch review, submission, delivery, and verification metrics for this supervisor.'
        );

        const supervisorRowHeight = drawField(
          'Supervisor ID',
          String(supervisorPerformanceData?.supervisorId ?? selectedCanvasser.id),
          margin,
          leftColumnWidth
        );
        y += supervisorRowHeight + 8;

        drawMetricGrid(supervisorPerformanceCards.map((card) => ({
          label: card.label,
          value: card.value,
        })));

        if (hasSupervisorPerformanceError) {
          drawNotice('Supervisor performance data was unavailable at export time. The report includes fallback values where necessary.');
        }

        if (supervisorPerformanceQuery.isLoading) {
          drawNotice('Supervisor performance data was still loading at export time. The report includes the values available in the modal at the time of export.');
        }
      }

      const safeFileName = selectedCanvasser.fullName
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '') || 'canvasser-details';

      pdf.save(`${safeFileName}-details.pdf`);
      toast.success('Canvasser PDF downloaded');
    } catch (error) {
      toast.error('Failed to export canvasser details');
    } finally {
      setIsExportingPdf(false);
    }
  };

  const handleAddSeats = () => {
    setShowSeatModal(true);
  };

  const handleRequestSeats = () => {
    addSeatsMutation.mutate(
      {
        method: "POST",
        url: "/partner/plan/addons/extra-seat",
        data: {
          quantity: seatQuantity
        }
      } as ApiRequestOptions,
      {
        onSuccess: (response) => {
          if (response.success) {
            toast.success(`${seatQuantity} seat(s) added successfully`);
            setRenewalMessage(response.data.renewalDescription);
            setShowSeatModal(false);
            setSeatQuantity(1);
            partnerQuery.refetch();
          }
        },
        onError: () => {
          toast.error('Failed to add seats. Please try again.');
        }
      }
    );
  };

  const seatUsagePercentage = ((partnerQuery.data?.data?.seatsTaken || 0) / (partnerQuery.data?.data?.seatsAllowed || 1)) * 100;
  const isAtLimit = seatUsagePercentage >= 100;
  const isNearLimit = !isSupervisor && seatUsagePercentage >= 95;

  return (
    <AdminLayout role={dashboardRole}>
      <div className="space-y-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-3xl blur-xl"></div>
          <div className="relative bg-white/70 backdrop-blur-xl border border-white/20 rounded-2xl p-5 shadow-lg">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6">
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2">
                  Manage Canvassers
                </h1>
                <p className="text-gray-600 ">
                  Manage your team of canvassers and track their performance
                </p>
              </div>

            </div>
          </div>

          {isNearLimit && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className={`relative rounded-2xl p-5 mt-5 shadow-xl border ${isAtLimit
                ? 'bg-red-50/90 backdrop-blur-xl border-red-200/50'
                : 'bg-amber-50/90 backdrop-blur-xl border-amber-200/50'
                }`}
            >
              <div className="flex items-center justify-between ">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isAtLimit ? 'bg-red-100' : 'bg-amber-100'
                    }`}>
                    {isAtLimit ? (
                      <BlockIcon className={`w-6 h-6 ${isAtLimit ? 'text-red-600' : 'text-amber-600'}`} />
                    ) : (
                      <svg className={`w-6 h-6 ${isAtLimit ? 'text-red-600' : 'text-amber-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                    )}
                  </div>
                  <div>
                    <h3 className={`font-semibold text-lg ${isAtLimit ? 'text-red-900' : 'text-amber-900'
                      }`}>
                      {isAtLimit ? 'Seat Limit Reached' : 'Seat Limit Warning'}
                    </h3>
                    <p className={`text-sm ${isAtLimit ? 'text-red-700' : 'text-amber-700'
                      }`}>
                      {isAtLimit
                        ? `You've reached your maximum of ${partnerQuery.data?.data?.seatsAllowed || 0} seats. Add more seats to continue inviting canvassers.`
                        : `You're using ${Math.round(seatUsagePercentage)}% of your ${partnerQuery.data?.data?.seatsAllowed || 0} seats. Consider adding more seats soon.`
                      }
                    </p>
                  </div>
                </div>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={handleAddSeats}
                  className={`px-6 py-3 rounded-xl font-semibold text-white shadow-lg transition-all duration-300 ${isAtLimit
                    ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700'
                    : 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600'
                    }`}
                >
                  <div className="flex items-center gap-2">
                    <AddIcon />
                    <span>Add More Seats</span>
                  </div>
                </motion.button>
              </div>
            </motion.div>
          )}

          {renewalMessage && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="relative bg-green-50/90 backdrop-blur-xl mt-5 border-green-200/50 rounded-2xl p-6 shadow-xl border"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircleIcon className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg text-green-900">Seats Added Successfully</h3>
                  <p className="text-sm text-green-700">{renewalMessage}</p>
                </div>
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="relative group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300 opacity-75"></div>
            <div className="relative bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-xl text-white shadow-lg">
                  <PeopleAltIcon />
                </div>
                {partnerQuery.data?.data?.seatsTaken && partnerQuery.data?.data?.seatsAllowed && (
                  <div className={`px-3 py-1 rounded-full text-xs font-semibold ${((partnerQuery.data?.data?.seatsTaken || 0) / (partnerQuery.data?.data?.seatsAllowed || 1)) * 100 >= 90 ? "bg-red-100 text-red-700 border-red-200/50" : "bg-green-100 text-green-700 border-green-200/50"} border `}>
                    {((partnerQuery.data?.data?.seatsTaken || 0) / (partnerQuery.data?.data?.seatsAllowed || 1)) * 100}%
                  </div>
                )}
              </div>
              <h3 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2">
                {partnerQuery.data?.data?.seatsTaken || 0} <span className="text-base text-gray-500">/ {partnerQuery.data?.data?.seatsAllowed || 0}</span>
              </h3>
              <p className="text-sm font-medium text-gray-600">
                Total Canvassers
              </p>
              <div className="mt-4 h-1 bg-gradient-to-r from-blue-500/20 to-cyan-500/20 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(((partnerQuery.data?.data?.seatsTaken || 0) / (partnerQuery.data?.data?.seatsAllowed || 1)) * 100, 100)}%` }}
                  transition={{ delay: 0.5, duration: 0.8 }}
                  className="h-full bg-gradient-to-r from-blue-500 to-cyan-600 rounded-full"
                />
              </div>

            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="relative group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300 opacity-75"></div>
            <div className="relative bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl text-white shadow-lg">
                  <HowToRegIcon />
                </div>
              </div>
              <h3 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2">
                {canvassers.filter((canvasser) => isCanvasserActive(canvasser)).length}
              </h3>
              <p className="text-sm font-medium text-gray-600">
                Active Canvassers
              </p>
              <div className="mt-4 h-1 bg-gradient-to-r from-green-500/20 to-emerald-500/20 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: "60%" }}
                  transition={{ delay: 0.6, duration: 0.8 }}
                  className="h-full bg-gradient-to-r from-green-500 to-emerald-600 rounded-full"
                />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="relative group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300 opacity-75"></div>
            <div className="relative bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-gradient-to-br from-purple-500 to-pink-600 rounded-xl text-white shadow-lg">
                  <GroupIcon />
                </div>
              </div>
              <h3 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2">
                {canvassers.length}
              </h3>
              <p className="text-sm font-medium text-gray-600">
                Total Members
              </p>
              <div className="mt-4 h-1 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: "85%" }}
                  transition={{ delay: 0.7, duration: 0.8 }}
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-600 rounded-full"
                />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="relative group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-2xl blur-xl group-hover:blur-2xl transition-all duration-300 opacity-75"></div>
            <div className="relative bg-white/80 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-[1.02]">
              <div className="flex justify-between items-start mb-4">
                <div className="p-3 bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl text-white shadow-lg">
                  <TrendingUpIcon />
                </div>
              </div>
              <h3 className="text-3xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-2">
                {activeSupervisors.length}
              </h3>
              <p className="text-sm font-medium text-gray-600">
                Active Supervisors
              </p>
              <div className="mt-4 h-1 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: "90%" }}
                  transition={{ delay: 0.8, duration: 0.8 }}
                  className="h-full bg-gradient-to-r from-amber-500 to-orange-600 rounded-full"
                />
              </div>
            </div>
          </motion.div>
        </div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="relative"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-indigo-500/10 to-blue-500/10 rounded-2xl blur-xl"></div>
          <div className="relative bg-white/70 backdrop-blur-xl border border-white/20 rounded-2xl p-6 shadow-xl">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <SearchIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search canvassers by name, email, or location..."
                  className="border border-gray-200/50 bg-white/50 backdrop-blur-sm rounded-xl w-full pl-12 pr-4 py-4 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all duration-300"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="w-full sm:w-48">
                <FormControl fullWidth size="medium">
                  <InputLabel className="bg-white/50 backdrop-blur-sm px-2">Status</InputLabel>
                  <Select
                    value={statusFilter}
                    label="Status"
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="bg-white/50 backdrop-blur-sm rounded-xl"
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </Select>
                </FormControl>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Loading State */}
        {isLoading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-2xl blur-xl"></div>
            <div className="relative bg-white/70 backdrop-blur-xl border border-white/20 rounded-2xl p-12 shadow-xl">
              <div className="text-center">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-16 h-16 mx-auto mb-6"
                >
                  <div className="w-full h-full border-4 border-blue-500/20 border-t-blue-500 rounded-full"></div>
                </motion.div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Loading canvassers...</h3>
                <p className="text-gray-600">Please wait while we fetch your team data</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Error State */}
        {isError && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-red-500/10 to-orange-500/10 rounded-2xl blur-xl"></div>
            <div className="relative bg-white/70 backdrop-blur-xl border border-white/20 rounded-2xl p-12 shadow-xl">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-6 bg-red-100 rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Error loading canvassers</h3>
                <p className="text-gray-600">Please try again later</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Canvassers Table */}
        {!isLoading && !isError && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-slate-500/10 to-gray-500/10 rounded-2xl blur-xl"></div>
            <div className="relative bg-white/70 backdrop-blur-xl border border-white/20 rounded-2xl shadow-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50/50 backdrop-blur-sm border-b border-gray-200/50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Member
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Role
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Contact
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Location
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Performance
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white/30 backdrop-blur-sm divide-y divide-gray-200/30">
                    {filteredCanvassers.map((canvasser, index) => (
                      <motion.tr
                        key={canvasser.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.7 + index * 0.05 }}
                        className="hover:bg-white/50 cursor-pointer transition-all duration-300"
                        onClick={() => handleShowDetails(canvasser)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center shadow-lg">
                              <PersonIcon className="w-6 h-6 text-white" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-semibold text-gray-900">{canvasser.fullName}</div>
                              <div className="text-sm text-gray-500">Joined {formatDate(canvasser.createdAt)}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex rounded-full border px-3 py-1 text-xs font-semibold shadow-sm ${getRoleColor(canvasser.role)}`}>
                            {formatRoleLabel(canvasser.role)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center gap-2">
                            <EmailIcon className="w-4 h-4 text-gray-400" />
                            {canvasser.email.length > 20 ? `${canvasser.email.substring(0, 20)}...` : canvasser.email}
                          </div>
                          <div className="text-sm text-gray-500 flex items-center gap-2">
                            <PhoneIcon className="w-4 h-4 text-gray-400" />
                            {canvasser.phone && canvasser.phone.length > 15 ? `${canvasser.phone.substring(0, 15)}...` : (canvasser.phone || 'No phone')}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 flex items-center gap-2">
                            <LocationOnIcon className="w-4 h-4 text-gray-400" />
                            {canvasser.lastLoginLocation || 'Unknown'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex px-3 py-1 text-xs font-semibold rounded-full ${getStatusColor(canvasser)} shadow-sm`}>
                              {getStatusLabel(canvasser)}
                            </span>
                            {canvasser.onShift ? (
                              <Chip label="On Shift" size="small" color="success" className="shadow-sm" />
                            ) : (
                              <Chip label="Off Shift" size="small" color="warning" className="shadow-sm" />
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            <div className="font-semibold">{canvasser.totalRegistrations} registrations</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                         
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              onClick={() => openManageModal(canvasser)}
                              className="px-3 py-1 rounded-lg transition-colors duration-200 text-xs font-medium bg-slate-500/10 text-slate-700 hover:bg-slate-500/20"
                            >
                              <span className="flex items-center gap-1">
                                <SettingsIcon className="w-4 h-4" />
                                Manage
                              </span>
                            </motion.button>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredCanvassers.length === 0 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="text-center py-16"
                >
                  <div className="w-20 h-20 mx-auto mb-6 bg-gray-100 rounded-full flex items-center justify-center">
                    <PersonIcon className="w-10 h-10 text-gray-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">
                    {canvassers.length === 0 ? 'No canvassers found' : 'No canvassers match your filters'}
                  </h3>
                  <p className="text-gray-600">
                    {canvassers.length === 0 ? 'Your team has no canvassers yet' : 'Try adjusting your search or filter criteria'}
                  </p>
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </div>


      {/* Canvasser Details Modal */}
      {showDetailsModal && selectedCanvasser && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowDetailsModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white/90 backdrop-blur-xl rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-hidden shadow-2xl border border-white/20"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative">
              {/* Modal Header */}
              <div className="relative bg-gradient-to-r from-neutral-600 to-gray-600 px-8 py-6 text-white">
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="relative z-10">
                  <div className="flex items-center justify-between">
                    <h2 className="text-2xl font-bold">Member Details</h2>
                    <div className="flex items-center gap-3">
                      <motion.button
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={handleExportCanvasserPdf}
                        disabled={isExportingPdf}
                        className="inline-flex items-center gap-2 rounded-xl bg-white/20 px-4 py-2.5 text-sm font-semibold text-white shadow-lg transition-colors hover:bg-white/30 disabled:cursor-not-allowed disabled:opacity-60"
                      >
                        <DownloadRoundedIcon className="h-5 w-5" />
                        {isExportingPdf ? 'Exporting...' : 'Export PDF'}
                      </motion.button>
                      <motion.button
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        onClick={handleCloseDetails}
                        className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                      >
                        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </motion.button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="max-h-[calc(90vh-96px)] overflow-y-auto p-8">
                {/* Profile Section */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full flex items-center justify-center shadow-xl">
                    <PersonIcon className="w-10 h-10 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{selectedCanvasser.fullName}</h3>
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex rounded-full border px-3 py-1 text-sm font-semibold shadow-sm ${getRoleColor(selectedCanvasser.role)}`}>
                        {formatRoleLabel(selectedCanvasser.role)}
                      </span>
                      <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(selectedCanvasser)} shadow-sm`}>
                        {getStatusLabel(selectedCanvasser)}
                      </span>
                      {selectedCanvasser.onShift && (
                        <Chip label="On Shift" size="small" color="success" className="shadow-sm" />
                      )}
                    </div>
                  </div>
                </div>

                <div className="mb-6 rounded-3xl border border-slate-200/70 bg-gradient-to-br from-slate-50 via-white to-blue-50/80 p-6 shadow-inner">
                  <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                      <div className="inline-flex items-center rounded-full border border-blue-200/70 bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-blue-700 shadow-sm">
                        Shift Activity Timeline
                      </div>
                      <h4 className="mt-3 text-xl font-bold text-slate-900">Tracking & Session Summary</h4>
                      <p className="mt-1 text-sm text-slate-600">
                        Aggregated partner field activity metrics for this member.
                      </p>
                    </div>
                    <div className="rounded-2xl border border-white/70 bg-white/80 px-4 py-3 shadow-sm">
                      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Tracking status</p>
                      <p className="mt-1 text-sm font-medium text-slate-800">
                        {trackingQuery.isLoading ? 'Loading timeline...' : trackingQuery.isError ? 'Fallback data' : 'Synced'}
                      </p>
                    </div>
                  </div>

                  <div className="mb-5 rounded-2xl border border-slate-200/70 bg-white/90 p-4 shadow-sm">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
                      <div className="grid flex-1 grid-cols-1 gap-4 md:grid-cols-2">
                        <div>
                          <label className="mb-1 block text-sm font-medium text-slate-700">
                            Start Date
                          </label>
                          <DatePicker
                            selected={trackingStartDate}
                            onChange={(date: Date | null) => setTrackingStartDate(date)}
                            selectsStart
                            startDate={trackingStartDate}
                            endDate={trackingEndDate}
                            maxDate={trackingEndDate || new Date()}
                            placeholderText="Select start date"
                            dateFormat="yyyy-MM-dd"
                            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>

                        <div>
                          <label className="mb-1 block text-sm font-medium text-slate-700">
                            End Date
                          </label>
                          <DatePicker
                            selected={trackingEndDate}
                            onChange={(date: Date | null) => setTrackingEndDate(date)}
                            selectsEnd
                            startDate={trackingStartDate}
                            endDate={trackingEndDate}
                            minDate={trackingStartDate || undefined}
                            maxDate={new Date()}
                            placeholderText="Select end date"
                            dateFormat="yyyy-MM-dd"
                            className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      </div>

                      <div className="flex gap-3">
                        <Button
                          type="button"
                          label="Filter"
                          variant="primary"
                          size="medium"
                          onClick={handleTrackingFilter}

                        />
                        <Button
                          type="button"
                          onClick={handleResetTrackingFilter}

                          variant="tertiary"
                          size="medium"
                          label='Today'
                        />
                      </div>
                    </div>

                    <p className="mt-3 text-sm text-slate-600">
                      {appliedTrackingRange
                        ? `Showing field activity from ${format(trackingStartDate || new Date(appliedTrackingRange.startDate), 'MMM dd, yyyy')} to ${format(trackingEndDate || new Date(appliedTrackingRange.endDate), 'MMM dd, yyyy')}.`
                        : `No date range selected. Showing field activity for today, ${format(new Date(), 'MMM dd, yyyy')}.`}
                    </p>
                  </div>

                  {trackingQuery.isLoading ? (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                      {Array.from({ length: 6 }).map((_, index) => (
                        <div
                          key={index}
                          className="animate-pulse rounded-2xl border border-white/70 bg-white/80 p-5 shadow-sm"
                        >
                          <div className="mb-4 flex items-center gap-3">
                            <div className="h-12 w-12 rounded-2xl bg-slate-200" />
                            <div className="space-y-2">
                              <div className="h-3 w-24 rounded bg-slate-200" />
                              <div className="h-4 w-20 rounded bg-slate-300" />
                            </div>
                          </div>
                          <div className="h-5 w-40 rounded bg-slate-200" />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                      {trackingCards.map((card) => {
                        const Icon = card.icon;

                        return (
                          <div
                            key={card.label}
                            className="group relative overflow-hidden rounded-2xl border border-white/70 bg-white/85 p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                          >
                            <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r opacity-40 ${card.accent}`}></div>
                            <div className="mb-5 flex items-start justify-between gap-4">
                              <div>
                                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">{card.label}</p>
                                <p className="mt-3 text-lg font-semibold leading-snug text-slate-900">{card.value}</p>
                              </div>
                              <div className={`flex h-12 w-12 items-center justify-center rounded-2xl border ${card.tone}`}>
                                <Icon className="h-5 w-5" />
                              </div>
                            </div>
                            {/* <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                              <div className={`h-full w-2/3 rounded-full bg-gradient-to-r opacity-40 ${card.accent}`}></div>
                            </div> */}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {trackingQuery.isError && (
                    <div className="mt-4 rounded-2xl border border-amber-200/70 bg-amber-50/90 p-4 text-sm text-amber-800 shadow-sm">
                      Live tracking data is currently unavailable. Fallback values are being shown.
                    </div>
                  )}
                </div>

                {/* Performance Section */}
                {isSelectedMemberCanvasser && (
                  <div className="mb-6 rounded-3xl border border-slate-200/70 bg-gradient-to-br from-slate-50 via-white to-purple-50/80 p-6 shadow-inner">
                    <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <div className="inline-flex items-center rounded-full border border-purple-200/70 bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-purple-700 shadow-sm">
                          Performance Analytics
                        </div>
                        <h4 className="mt-3 text-xl font-bold text-slate-900">Canvasser Performance</h4>
                        <p className="mt-1 text-sm text-slate-600">
                          Key performance indicators and quality metrics for this canvasser.
                        </p>
                      </div>
                      <div className="rounded-2xl border border-white/70 bg-white/80 px-4 py-3 shadow-sm">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Risk Tier</p>
                        <p className="mt-1 text-sm font-medium text-slate-800">
                          {performanceData?.riskTier || 'UNKNOWN'}
                        </p>
                      </div>
                    </div>

                    {performanceQuery.isLoading ? (
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {Array.from({ length: 6 }).map((_, index) => (
                          <div
                            key={index}
                            className="animate-pulse rounded-2xl border border-white/70 bg-white/80 p-5 shadow-sm"
                          >
                            <div className="mb-4 flex items-center gap-3">
                              <div className="h-12 w-12 rounded-2xl bg-slate-200" />
                              <div className="space-y-2">
                                <div className="h-3 w-24 rounded bg-slate-200" />
                                <div className="h-4 w-20 rounded bg-slate-300" />
                              </div>
                            </div>
                            <div className="h-5 w-40 rounded bg-slate-200" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {performanceCards.map((card) => {
                          const Icon = card.icon;

                          return (
                            <div
                              key={card.label}
                              className="group relative overflow-hidden rounded-2xl border border-white/70 bg-white/85 p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                            >
                              <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r opacity-40 ${card.accent}`}></div>
                              <div className="mb-5 flex items-start justify-between gap-4">
                                <div>
                                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">{card.label}</p>
                                  <p className="mt-3 text-lg font-semibold leading-snug text-slate-900">{card.value}</p>
                                </div>
                                <div className={`flex h-12 w-12 items-center justify-center rounded-2xl border ${card.tone}`}>
                                  <Icon className="h-5 w-5" />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {performanceQuery.isError && (
                      <div className="mt-4 rounded-2xl border border-amber-200/70 bg-amber-50/90 p-4 text-sm text-amber-800 shadow-sm">
                        Performance data is currently unavailable. Please try again later.
                      </div>
                    )}
                  </div>
                )}

                {isSelectedMemberSupervisor && (
                  <div className="mb-6 rounded-3xl border border-slate-200/70 bg-gradient-to-br from-slate-50 via-white to-violet-50/80 p-6 shadow-inner">
                    <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                      <div>
                        <div className="inline-flex items-center rounded-full border border-violet-200/70 bg-white/80 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.24em] text-violet-700 shadow-sm">
                          Performance Analytics
                        </div>
                        <h4 className="mt-3 text-xl font-bold text-slate-900">Supervisor Performance</h4>
                        <p className="mt-1 text-sm text-slate-600">
                          Batch review, submission, delivery, and verification metrics for this supervisor.
                        </p>
                      </div>
                      <div className="rounded-2xl border border-white/70 bg-white/80 px-4 py-3 shadow-sm">
                        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Supervisor ID</p>
                        <p className="mt-1 text-sm font-medium text-slate-800">
                          {supervisorPerformanceData?.supervisorId ?? selectedCanvasser.id}
                        </p>
                      </div>
                    </div>

                    {supervisorPerformanceQuery.isLoading ? (
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {Array.from({ length: 7 }).map((_, index) => (
                          <div
                            key={index}
                            className="animate-pulse rounded-2xl border border-white/70 bg-white/80 p-5 shadow-sm"
                          >
                            <div className="mb-4 flex items-center gap-3">
                              <div className="h-12 w-12 rounded-2xl bg-slate-200" />
                              <div className="space-y-2">
                                <div className="h-3 w-24 rounded bg-slate-200" />
                                <div className="h-4 w-20 rounded bg-slate-300" />
                              </div>
                            </div>
                            <div className="h-5 w-40 rounded bg-slate-200" />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
                        {supervisorPerformanceCards.map((card) => {
                          const Icon = card.icon;

                          return (
                            <div
                              key={card.label}
                              className="group relative overflow-hidden rounded-2xl border border-white/70 bg-white/85 p-5 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                            >
                              <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r opacity-40 ${card.accent}`}></div>
                              <div className="mb-5 flex items-start justify-between gap-4">
                                <div>
                                  <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">{card.label}</p>
                                  <p className="mt-3 text-lg font-semibold leading-snug text-slate-900">{card.value}</p>
                                </div>
                                <div className={`flex h-12 w-12 items-center justify-center rounded-2xl border ${card.tone}`}>
                                  <Icon className="h-5 w-5" />
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {hasSupervisorPerformanceError && (
                      <div className="mt-4 rounded-2xl border border-amber-200/70 bg-amber-50/90 p-4 text-sm text-amber-800 shadow-sm">
                        Supervisor performance data is currently unavailable. Please try again later.
                      </div>
                    )}
                  </div>
                )}

                {/* Information Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-4">
                  <div className="space-y-6">
                    <div className="bg-gray-50/50 backdrop-blur-sm rounded-xl p-4">
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <label className="block text-sm font-medium text-gray-500">Email Address</label>
                        <div className="flex items-center gap-2">
                          {editingField === 'email' ? (
                            <>
                              <button
                                type="button"
                                onClick={() => handleSaveContactField('email')}
                                disabled={updateCanvasserMutation.isPending}
                                className="rounded-full p-2 text-emerald-600 transition-colors hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <CheckRoundedIcon className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={handleCancelEditing}
                                disabled={updateCanvasserMutation.isPending}
                                className="rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <CloseRoundedIcon className="h-4 w-4" />
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleStartEditing('email')}
                              className="rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
                            >
                              <EditOutlinedIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                          <EmailIcon className="w-5 h-5 text-blue-600" />
                        </div>
                        {editingField === 'email' ? (
                          <input
                            type="email"
                            value={editableEmail}
                            onChange={(event) => setEditableEmail(event.target.value)}
                            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter email address"
                          />
                        ) : (
                          <span className="text-sm font-medium truncate text-gray-900">{selectedCanvasser.email}</span>
                        )}
                      </div>
                    </div>

                    <div className="bg-gray-50/50 backdrop-blur-sm rounded-xl p-4">
                      <div className="mb-2 flex items-center justify-between gap-3">
                        <label className="block text-sm font-medium text-gray-500">Phone Number</label>
                        <div className="flex items-center gap-2">
                          {editingField === 'phone' ? (
                            <>
                              <button
                                type="button"
                                onClick={() => handleSaveContactField('phone')}
                                disabled={updateCanvasserMutation.isPending}
                                className="rounded-full p-2 text-emerald-600 transition-colors hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <CheckRoundedIcon className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={handleCancelEditing}
                                disabled={updateCanvasserMutation.isPending}
                                className="rounded-full p-2 text-slate-500 transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <CloseRoundedIcon className="h-4 w-4" />
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleStartEditing('phone')}
                              className="rounded-full text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"
                            >
                              <EditOutlinedIcon className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <PhoneIcon className="w-5 h-5 text-green-600" />
                        </div>
                        {editingField === 'phone' ? (
                          <input
                            type="text"
                            value={editablePhone}
                            onChange={(event) => setEditablePhone(event.target.value)}
                            className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            placeholder="Enter phone number"
                          />
                        ) : (
                          <span className="text-sm font-medium text-gray-900">{selectedCanvasser.phone || 'No phone'}</span>
                        )}
                      </div>
                    </div>

                    <div className="bg-gray-50/50 backdrop-blur-sm rounded-xl p-4">
                      <label className="text-sm font-medium text-gray-500 block mb-2">Location</label>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                          <LocationOnIcon className="w-5 h-5 text-purple-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-900">{selectedCanvasser.lastLoginLocation || 'Unknown'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="bg-gray-50/50 backdrop-blur-sm rounded-xl p-4">
                      <label className="text-sm font-medium text-gray-500 block mb-2">Join Date</label>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                          <EventIcon className="w-5 h-5 text-amber-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-900">{formatDate(selectedCanvasser.createdAt)}</span>
                      </div>
                    </div>

                    <div className="bg-gray-50/50 backdrop-blur-sm rounded-xl p-4">
                      <label className="text-sm font-medium text-gray-500 block mb-2">Performance</label>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                          <TrendingUpIcon className="w-5 h-5 text-indigo-600" />
                        </div>
                        <div>
                          <span className="text-2xl font-bold text-gray-900">{selectedCanvasser.totalRegistrations}</span>
                          <span className="text-sm text-gray-500 ml-2">total registrations</span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50/50 backdrop-blur-sm rounded-xl p-4">
                      <label className="text-sm font-medium text-gray-500 block mb-2">Shift Status</label>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                          <EventIcon className="w-5 h-5 text-orange-600" />
                        </div>
                        <span className="text-sm font-medium text-gray-900">
                          {selectedCanvasser.onShift ? 'On Shift' : 'Off Shift'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-4 pt-3 border-t border-gray-200">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleExportCanvasserPdf}
                    disabled={isExportingPdf}
                    className="px-6 py-3 bg-slate-900 text-white rounded-xl font-semibold shadow-lg hover:bg-slate-800 transition-colors disabled:cursor-not-allowed disabled:bg-slate-400"
                  >
                    <span className="flex items-center gap-2">
                      <DownloadRoundedIcon className="w-5 h-5" />
                      {isExportingPdf ? 'Exporting...' : 'Export PDF'}
                    </span>
                  </motion.button>
                  {/* <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      handleDeleteCanvasser(selectedCanvasser.id);
                      setShowDetailsModal(false);
                    }}
                    className="px-6 py-3 bg-red-500 text-white rounded-xl font-semibold shadow-lg hover:bg-red-600 transition-colors"
                  >
                    Delete
                  </motion.button> */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => {
                      openManageModal(selectedCanvasser);
                    }}
                    className="px-6 py-3 rounded-xl font-semibold shadow-lg transition-colors bg-slate-700 text-white hover:bg-slate-800"
                  >
                    <span className="flex items-center gap-2">
                      <SettingsIcon className="w-5 h-5" />
                      Manage
                    </span>
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowDetailsModal(false)}
                    className="px-6 py-3 bg-gray-200 text-gray-700 rounded-xl font-semibold shadow-lg hover:bg-gray-300 transition-colors ml-auto"
                  >
                    Close
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Manage Member Modal */}
      {showManageModal && manageTargetMember && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          onClick={closeManageFlow}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 16 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 16 }}
            className="bg-white rounded-3xl w-full max-w-2xl shadow-2xl border border-white/20 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-slate-700 to-slate-900 px-8 py-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold">Manage Member</h2>
                  <p className="text-slate-200 text-sm mt-1">{manageTargetMember.fullName}</p>
                </div>
                <button
                  onClick={closeManageFlow}
                  className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                >
                  <CloseRoundedIcon />
                </button>
              </div>
            </div>

            <div className="p-8 space-y-5">
              {isCanvasserPermanentlyDeactivated(manageTargetMember) ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-5">
                  <div className="flex items-start gap-3">
                    <WarningAmberIcon className="text-red-600 mt-0.5" />
                    <div>
                      <h3 className="font-semibold text-red-900">Member permanently deactivated</h3>
                      <p className="text-sm text-red-800 mt-2 leading-relaxed">
                        This member has been deactivated and their seat has been released. This action cannot be reversed from your partner dashboard.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {!isSupervisor && isCanvasserMember(manageTargetMember) && (
                    <div className="rounded-2xl border border-indigo-200 bg-indigo-50 p-5">
                      <h3 className="font-semibold text-indigo-900 mb-2">Upgrade to Supervisor</h3>
                      <p className="text-sm text-indigo-800 leading-relaxed mb-4">
                        Promote this canvasser so they can access supervisor tools for managing field operations.
                      </p>
                      <button
                        onClick={handleUpgradeActionRequest}
                        className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition-colors"
                      >
                        Upgrade to Supervisor
                      </button>
                    </div>
                  )}

                  <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
                    <h3 className="font-semibold text-amber-900 mb-2">Suspend / Unsuspend</h3>
                    <p className="text-sm text-amber-800 leading-relaxed mb-4">
                      Temporarily suspend this member. Their seat remains assigned to your organization and you can unsuspend them later to restore access.
                    </p>
                    {isCanvasserSuspended(manageTargetMember) ? (
                      <button
                        onClick={() => handleSuspendActionRequest('unsuspend')}
                        className="px-4 py-2 rounded-xl bg-green-600 text-white text-sm font-semibold hover:bg-green-700 transition-colors"
                      >
                        Unsuspend Member
                      </button>
                    ) : (
                      <button
                        onClick={() => handleSuspendActionRequest('suspend')}
                        className="px-4 py-2 rounded-xl bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 transition-colors"
                      >
                        Suspend Member
                      </button>
                    )}
                  </div>

                  <div className="rounded-2xl border border-red-200 bg-red-50 p-5">
                    <h3 className="font-semibold text-red-900 mb-2">Deactivate Member</h3>
                    <p className="text-sm text-red-800 leading-relaxed mb-4">
                      Permanently deactivate this member and release their seat. The seat becomes available for another canvasser. This operation is irreversible.
                    </p>
                    <button
                      onClick={handleDeactivateActionRequest}
                      className="px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors"
                    >
                      Deactivate Member
                    </button>
                  </div>
                </>
              )}

              <button
                onClick={closeManageFlow}
                className="w-full px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-colors"
              >
                Close
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Suspend / Unsuspend Confirmation Modal */}
      {showSuspendConfirmModal && manageTargetMember && pendingSuspendAction && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
          onClick={closeManageFlow}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl w-full max-w-lg shadow-2xl border border-white/20 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`px-8 py-6 text-white ${pendingSuspendAction === 'suspend' ? 'bg-gradient-to-r from-amber-600 to-orange-600' : 'bg-gradient-to-r from-green-600 to-emerald-600'}`}>
              <h2 className="text-xl font-bold">
                {pendingSuspendAction === 'suspend' ? 'Confirm Suspension' : 'Confirm Unsuspension'}
              </h2>
            </div>
            <div className="p-8 space-y-5">
              <p className="text-sm text-gray-700 leading-relaxed">
                {pendingSuspendAction === 'suspend' ? (
                  <>
                    You are about to suspend <span className="font-semibold">{manageTargetMember.fullName}</span>.
                    They will lose access, but their seat will remain assigned to your organization and can be restored later.
                  </>
                ) : (
                  <>
                    You are about to unsuspend <span className="font-semibold">{manageTargetMember.fullName}</span>.
                    They will regain access while keeping the same assigned seat.
                  </>
                )}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowSuspendConfirmModal(false);
                    setPendingSuspendAction(null);
                    setShowManageModal(true);
                  }}
                  className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmSuspendAction}
                  disabled={suspendUserMutation.isPending || unsuspendUserMutation.isPending}
                  className={`flex-1 px-4 py-3 rounded-xl font-semibold text-white disabled:opacity-60 ${pendingSuspendAction === 'suspend' ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'}`}
                >
                  {suspendUserMutation.isPending || unsuspendUserMutation.isPending
                    ? 'Processing...'
                    : pendingSuspendAction === 'suspend'
                      ? 'Yes, Suspend'
                      : 'Yes, Unsuspend'}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Upgrade Confirmation Modal */}
      {showUpgradeConfirmModal && manageTargetMember && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
          onClick={closeManageFlow}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl w-full max-w-lg shadow-2xl border border-white/20 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-indigo-600 to-slate-800 px-8 py-6 text-white">
              <h2 className="text-xl font-bold">Confirm Supervisor Upgrade</h2>
            </div>
            <div className="p-8 space-y-5">
              <p className="text-sm text-gray-700 leading-relaxed">
                You are about to upgrade <span className="font-semibold">{manageTargetMember.fullName}</span> from canvasser to supervisor.
                They will gain supervisor permissions for your organization.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowUpgradeConfirmModal(false);
                    setShowManageModal(true);
                  }}
                  className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmUpgradeAction}
                  disabled={upgradeMemberRoleMutation.isPending}
                  className="flex-1 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-semibold disabled:opacity-60"
                >
                  {upgradeMemberRoleMutation.isPending ? 'Processing...' : 'Yes, Upgrade'}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Deactivate Confirmation Modal */}
      {showDeactivateConfirmModal && manageTargetMember && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
          onClick={closeManageFlow}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-3xl w-full max-w-lg shadow-2xl border border-white/20 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-r from-red-600 to-rose-700 px-8 py-6 text-white">
              <h2 className="text-xl font-bold">Confirm Permanent Deactivation</h2>
            </div>
            <div className="p-8 space-y-5">
              <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
                <div className="flex items-start gap-3">
                  <WarningAmberIcon className="text-red-600 mt-0.5" />
                  <div className="text-sm text-red-800 leading-relaxed">
                    <p className="mb-2">
                      You are about to permanently deactivate <span className="font-semibold">{manageTargetMember.fullName}</span>.
                    </p>
                    <ul className="list-disc pl-5 space-y-1">
                      <li>The member account will be deactivated.</li>
                      <li>Their seat will be released immediately.</li>
                      <li>The released seat can be used to add another canvasser.</li>
                      <li>This action is irreversible.</li>
                    </ul>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeactivateConfirmModal(false);
                    setShowManageModal(true);
                  }}
                  className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDeactivateAction}
                  disabled={deactivateMemberMutation.isPending}
                  className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold disabled:opacity-60"
                >
                  {deactivateMemberMutation.isPending ? 'Processing...' : 'Yes, Deactivate'}
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Add Seats Modal */}
      {showSeatModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => setShowSeatModal(false)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="bg-white/95 backdrop-blur-xl rounded-3xl w-full max-w-lg max-h-[90vh] overflow-hidden shadow-2xl border border-white/20"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="relative bg-gradient-to-r from-indigo-600 to-purple-600 px-8 py-6 text-white">
              <div className="absolute inset-0 bg-black/10"></div>
              <div className="relative z-10">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold mb-1">Add More Seats</h2>
                    <p className="text-indigo-100 text-sm">Expand your team capacity</p>
                  </div>
                  <motion.button
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    onClick={() => setShowSeatModal(false)}
                    className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </motion.button>
                </div>
              </div>
            </div>

            <div className="p-8">
              {/* Disclaimer */}
              <div className="bg-amber-50/90 backdrop-blur-sm border border-amber-200/50 rounded-2xl p-6 mb-6">
                <div className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-amber-900 mb-2">Billing Disclaimer</h3>
                    <p className="text-sm text-amber-800 leading-relaxed">
                      You will be charged immediately for the extra seats via your saved payment method.
                      A prorated charge will be applied for the remainder of your current billing cycle.
                      Starting next renewal, your invoice will include the full monthly price for all extra seats.
                    </p>
                  </div>
                </div>
              </div>

              {/* Seat Quantity Input */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Number of Seats to Add
                </label>
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={seatQuantity}
                    onChange={(e) => setSeatQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-full px-4 py-4 bg-gray-50/50 backdrop-blur-sm border border-gray-200/50 rounded-2xl focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:border-indigo-500/50 transition-all duration-300 text-lg font-medium text-center"
                    placeholder="Enter number of seats"
                  />
                  <div className="absolute right-4 top-1/2 transform -translate-y-1/2 flex gap-2">
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setSeatQuantity(Math.max(1, seatQuantity - 1))}
                      className="w-8 h-8 bg-gray-200 hover:bg-gray-300 rounded-lg flex items-center justify-center transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                      </svg>
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      onClick={() => setSeatQuantity(Math.min(50, seatQuantity + 1))}
                      className="w-8 h-8 bg-gray-200 hover:bg-gray-300 rounded-lg flex items-center justify-center transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                    </motion.button>
                  </div>
                </div>
                {/* <p className="text-xs text-gray-500 mt-2 text-center">
                  Each seat costs $20.00/month (billed annually)
                </p> */}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowSeatModal(false)}
                  className="flex-1 px-6 py-4 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-2xl font-semibold transition-colors duration-200"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleRequestSeats}
                  disabled={addSeatsMutation.isPending}
                  className="flex-1 px-6 py-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white rounded-2xl font-semibold shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {addSeatsMutation.isPending ? (
                    <div className="flex items-center justify-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      <span>Processing...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center gap-2">
                      <span>Request {seatQuantity} Seat{seatQuantity > 1 ? 's' : ''}</span>
                      <AddIcon className="w-5 h-5" />
                    </div>
                  )}
                </motion.button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AdminLayout>
  );
}
